import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { ClientsFacade } from '@facades/clients.facade';
import { ParametersFacade } from '@facades/parameters.facade';
import { ProductsFacade } from '@facades/products.facade';
import { TenantsFacade } from '@facades/tenants.facade';
import { SellerOrderService } from '@services/seller-order.service';
import { SellerOrderStore } from '@stores/seller-order.store';

import type { IClient } from '@interfaces/client';
import type { IFiscalCalculation } from '@interfaces/fiscal';
import type { IProduct } from '@interfaces/product';
import {
	ESellerOrderStatus,
	ESellerOrderStep,
	type ISellerOrder,
	type ISellerOrderAddItemOptions,
	type ISellerOrderDraft,
	type ISellerOrderFiscalSubmit,
	type ISellerOrderItem,
	type ISellerOrderPayment,
	type ISellerOrderResumeResult,
	type ISellerOrderShipment,
} from '@interfaces/seller-order';
import { shouldBlockParentProductSale } from '@utils/parent-product-sale.util';
import { resolveSaleMultiplier } from '@utils/sale-multiplier.util';
import { getDefaultStoreId, isProductSellable } from '@utils/stock.util';

@Injectable({
	providedIn: 'root',
})
export class SellerOrderFacade {
	readonly state$ = this.store.state$.pipe(shareReplay({ refCount: true }));
	private pendingEntry: 'new' | 'resume' | null = null;
	private readonly entryIntent = new Subject<'new' | 'resume'>();
	/** Fired when Novo pedido or Continuar rascunho is chosen, including on the open screen. */
	readonly entryIntent$ = this.entryIntent.asObservable();

	constructor(
		private service: SellerOrderService,
		private store: SellerOrderStore,
		private clientsFacade: ClientsFacade,
		private productsFacade: ProductsFacade,
		private parametersFacade: ParametersFacade,
		private tenantsFacade: TenantsFacade
	) {}

	goToBuild() {
		this.store.setStep(ESellerOrderStep.BUILD);
	}

	goToFinalize() {
		this.store.setStep(ESellerOrderStep.FINALIZE);
	}

	selectClient(client: IClient) {
		this.store.setClient(client);
	}

	clearClient() {
		this.store.setClient(null);
	}

	setPriceList(priceListId: number | undefined, priceList?: string) {
		this.store.setPriceList(priceListId, priceList);
	}

	setItemPriceList(
		productId: number,
		priceListId: number | undefined,
		priceList?: string,
		packingId?: number | null
	) {
		this.store.setItemPriceList(productId, priceListId, priceList, packingId);
	}

	setOrderType(
		orderTypeId: number | undefined,
		orderType?: string,
		generatesFlexBalance = false,
		flags?: { isDevolution?: boolean; isQuote?: boolean }
	) {
		this.store.setOrderType(
			orderTypeId,
			orderType,
			generatesFlexBalance,
			flags
		);
	}

	setIgnoreMinimumPrice(ignore: boolean) {
		this.store.setIgnoreMinimumPrice(ignore);
	}

	setExpiresAt(expiresAt: string) {
		this.store.setExpiresAt(expiresAt);
	}

	ensureMinimumQuantities() {
		this.store.ensureMinimumQuantities();
	}

	distributeShipmentPrice(shipmentPrice: number) {
		this.store.distributeShipmentPrice(shipmentPrice);
	}

	applyOrderDiscountPercentage(discountPercentage: number) {
		this.store.applyOrderDiscountPercentage(discountPercentage);
	}

	applyOrderDiscountValue(discount: number) {
		this.store.applyOrderDiscountValue(discount);
	}

	replaceItemProduct(
		productId: number,
		product: IProduct,
		packingId?: number | null
	) {
		this.store.replaceItemProduct(productId, product, packingId);
	}

	addProduct(
		product: IProduct,
		options: ISellerOrderAddItemOptions | number = 1
	) {
		if (typeof options === 'number') {
			this.store.addItem(product, { quantity: options });
			return;
		}
		this.store.addItem(product, options);
	}

	applyItemTaxes(calculation: IFiscalCalculation | null) {
		this.store.applyItemTaxes(calculation);
	}

	updateQuantity(
		productId: number,
		quantity: number,
		packingId?: number | null
	) {
		this.store.updateItemQuantity(productId, quantity, packingId);
	}

	updatePrice(productId: number, price: number) {
		this.store.updateItemPrice(productId, price);
	}

	updateDiscount(productId: number, discountPercentage: number) {
		this.store.updateItemDiscount(productId, discountPercentage);
	}

	updateShipmentPrice(
		productId: number,
		shipmentPrice: number,
		packingId?: number | null
	) {
		this.store.updateItemShipmentPrice(productId, shipmentPrice, packingId);
	}

	removeProduct(productId: number, packingId?: number | null) {
		this.store.removeItem(productId, packingId);
	}

	updateShipment(shipment: Partial<ISellerOrderShipment>) {
		this.store.updateShipment(shipment);
	}

	updatePayment(payment: Partial<ISellerOrderPayment>) {
		this.store.updatePayment(payment);
	}

	loadDrafts() {
		return this.service
			.getDrafts()
			.pipe(tap(drafts => this.store.setDrafts(drafts)));
	}

	requestEntry(intent: 'new' | 'resume') {
		this.pendingEntry = intent;
		this.entryIntent.next(intent);
	}

	consumeEntry(): 'new' | 'resume' | null {
		const intent = this.pendingEntry;
		this.pendingEntry = null;
		return intent;
	}

	getDraft() {
		return this.service.getDraft();
	}

	saveDraft() {
		const order = this.store.snapshot.order;
		this.store.setSaving(true);

		return this.service.saveDraft(order).pipe(
			tap(saved => {
				const hasContent = Boolean(saved.clientId || saved.items.length);
				this.store.loadOrder(saved);
				this.store.setDrafts(hasContent ? [this.toDraftSummary(saved)] : []);
				this.store.setDraftSaved(hasContent ? new Date() : null);
			})
		);
	}

	/**
	 * Reopens the single draft, refreshes client and products, and drops lines
	 * that no longer exist or break a sale rule. Warnings are returned for the UI.
	 */
	resumeDraft() {
		return this.service.getDraft().pipe(
			switchMap(draft => {
				if (!draft || (!draft.clientId && !draft.items?.length)) {
					return of<ISellerOrderResumeResult>({
						applied: false,
						warnings: [],
					});
				}
				return this.revalidateDraft(draft);
			}),
			catchError(() =>
				of<ISellerOrderResumeResult>({
					applied: false,
					warnings: ['Não foi possível reabrir o rascunho. Tente novamente.'],
				})
			),
			tap(result => {
				if (!result.applied || !result.order) return;
				this.store.loadOrder(result.order);
				const hasContent = Boolean(
					result.order.clientId || result.order.items.length
				);
				if (!hasContent) {
					this.service.deleteDraft().subscribe();
					this.store.setDrafts([]);
					this.store.setDraftSaved(null);
					return;
				}
				this.service.saveDraft(result.order).subscribe(saved => {
					this.store.setDrafts([this.toDraftSummary(saved)]);
					this.store.setDraftSaved(new Date());
				});
			})
		);
	}

	deleteDraft() {
		return this.service.deleteDraft().pipe(
			tap(() => {
				this.store.setDrafts([]);
				this.store.setDraftSaved(null);
			})
		);
	}

	submitOrder(fiscal?: ISellerOrderFiscalSubmit) {
		this.store.setSubmitting(true);
		return this.service.submitOrder(this.store.snapshot.order, fiscal).pipe(
			tap({
				next: () => {
					this.store.setSubmitting(false);
					this.store.reset();
					this.loadDrafts().subscribe();
				},
				error: () => this.store.setSubmitting(false),
			})
		);
	}

	repeatLastOrder() {
		return this.resumeDraft();
	}

	reset() {
		this.store.reset();
	}

	private toDraftSummary(order: ISellerOrder): ISellerOrderDraft {
		return {
			id: order.id ?? 1,
			clientName: order.client?.name,
			total: order.total ?? 0,
			itemCount: order.items?.length ?? 0,
			updatedAt: order.updatedAt ?? new Date(),
		};
	}

	private revalidateDraft(draft: ISellerOrder) {
		const client$ = draft.clientId
			? this.clientsFacade.getClientById(draft.clientId).pipe(
					map(client => ({ client, missing: false, failed: false })),
					catchError((err: HttpErrorResponse) =>
						of({
							client:
								err?.status === 404
									? null
									: draft.client ?? (null as IClient | null),
							missing: err?.status === 404,
							failed: err?.status !== 404,
						})
					)
			  )
			: of({ client: null as IClient | null, missing: false, failed: false });

		const productCalls = (draft.items || []).map(item =>
			this.productsFacade.getProductById(item.productId).pipe(
				map(product => ({
					item,
					product,
					missing: false,
					failed: false,
				})),
				catchError((err: HttpErrorResponse) =>
					of({
						item,
						product:
							err?.status === 404
								? null
								: item.product ?? (null as IProduct | null),
						missing: err?.status === 404,
						failed: err?.status !== 404,
					})
				)
			)
		);

		return forkJoin({
			clientResult: client$,
			products: productCalls.length ? forkJoin(productCalls) : of([]),
		}).pipe(
			map(({ clientResult, products }) => {
				const warnings: string[] = [];
				let client = clientResult.client;

				if (draft.clientId && clientResult.failed) {
					warnings.push(
						'Não foi possível confirmar se o cliente ainda existe. Revise antes de enviar.'
					);
				} else if (draft.clientId && clientResult.missing) {
					warnings.push(
						'O cliente deste rascunho não existe mais e foi removido.'
					);
					client = null;
				} else if (client?.inactivatedAt) {
					warnings.push(
						'O cliente deste rascunho está inativo e foi removido.'
					);
					client = null;
				} else if (client?.isRestricted) {
					warnings.push(
						'Este cliente possui restrição. O pedido pode continuar, mas revise com atenção.'
					);
				}

				const items: ISellerOrderItem[] = [];
				for (const row of products) {
					const name = this.productLabel(row.item, row.product);
					if (row.failed) {
						warnings.push(
							`Não foi possível validar o produto ${name}. Confira se ele ainda pode ser vendido.`
						);
						if (row.product) {
							items.push({ ...row.item, product: row.product });
						}
						continue;
					}
					if (row.missing || !row.product) {
						warnings.push(
							`O produto ${name} não existe mais e foi removido do rascunho.`
						);
						continue;
					}

					const blocked = this.blockingRule(row.product, row.item, name);
					if (blocked) {
						warnings.push(blocked);
						continue;
					}

					warnings.push(...this.reviewWarnings(row.product, row.item, name));
					items.push({
						...row.item,
						product: row.product,
						sellingUnit: row.product.sellingUnit ?? row.item.sellingUnit,
					});
				}

				let shipment = draft.shipment;
				const addressId = shipment?.addressId ?? shipment?.address?.id;
				if (client && addressId != null) {
					const addressStillExists = client.addresses?.some(
						address => address.id === addressId
					);
					if (!addressStillExists) {
						warnings.push(
							'O endereço de entrega não existe mais neste cliente.'
						);
						shipment = {
							...shipment,
							addressId: undefined,
							address: undefined,
						};
					}
				}

				const order: ISellerOrder = {
					...draft,
					id: 1,
					status: ESellerOrderStatus.DRAFT,
					client: client ?? undefined,
					clientId:
						client?.id ?? (clientResult.failed ? draft.clientId : undefined),
					items,
					shipment,
				};

				return { applied: true, warnings, order };
			})
		);
	}

	private productLabel(item: ISellerOrderItem, product?: IProduct | null) {
		return (
			product?.name ||
			item.product?.name ||
			item.product?.sku ||
			`#${item.productId}`
		);
	}

	/** Rules that make the saved line unsellable. The item is dropped. */
	private blockingRule(
		product: IProduct,
		item: ISellerOrderItem,
		name: string
	): string | null {
		if (product.inactivatedAt) {
			return `O produto ${name} está inativo e foi removido do rascunho.`;
		}

		const blockParent = shouldBlockParentProductSale(
			this.tenantsFacade.hasFeatures('variations'),
			this.parametersFacade.parameters?.allowSellingParentProduct,
			product.variations?.length
		);
		if (blockParent && !item.variationLabel) {
			return `O produto ${name} só pode ser vendido pela variação e foi removido. Selecione a variação de novo.`;
		}

		const packingEnabled = this.tenantsFacade.hasFlag('enableProductPacking');
		if (packingEnabled && product.packings?.length) {
			if (item.packingId == null) {
				return `O produto ${name} exige embalagem e foi removido. Selecione a embalagem de novo.`;
			}
			const packingExists = product.packings.some(
				packing => packing.id === item.packingId
			);
			if (!packingExists) {
				return `A embalagem do produto ${name} não existe mais e o item foi removido.`;
			}
		}

		return null;
	}

	/** Rules the seller can still fix on the line. The item stays. */
	private reviewWarnings(
		product: IProduct,
		item: ISellerOrderItem,
		name: string
	): string[] {
		const warnings: string[] = [];
		const parameters = this.parametersFacade.parameters;
		const sellable = isProductSellable(
			product,
			!!parameters?.allowSellingWithoutStock,
			getDefaultStoreId(parameters)
		);
		if (!sellable) {
			warnings.push(`O produto ${name} está sem estoque.`);
		}

		const step = resolveSaleMultiplier(product.saleMultiplier);
		const units = item.quantity / step;
		if (Math.abs(units - Math.round(units)) > 1e-6) {
			warnings.push(
				`A quantidade de ${name} não respeita o múltiplo de venda (${step}).`
			);
		}

		const packing = item.packingId
			? product.packings?.find(entry => entry.id === item.packingId)
			: undefined;
		const prices = packing?.prices?.length
			? packing.prices
			: product.prices || [];
		if (
			item.priceListId != null &&
			prices.length > 0 &&
			!prices.some(price => price.priceListId === item.priceListId)
		) {
			warnings.push(
				`O produto ${name} não tem preço na tabela deste rascunho. Confira o valor.`
			);
		}

		return warnings;
	}
}
